import { useLayoutEffect, useRef, type RefObject } from 'react';
import { toast } from 'sonner';
import {
  CARD_LABEL_ABOVE_BELOW_PX,
  columnLabels,
  decideTableLayout,
  rememberTableWidths,
  sameTableContent,
  tableToHtml,
  tableToTsv,
  type TableLayout,
  type TableMemory,
  type TableSignature,
} from '~/lib/adaptive-table';

/**
 * FRONT-2539: the page half of adaptive tables (the pure half and the rules
 * live in `~/lib/adaptive-table.ts`). Runs on a `MarkdownRenderer` root
 * after React has put the sanitized reply into it, and before paint:
 *
 * 1. wraps each top-level table in a `.md-table` box with a Copy table
 *    button, labels every body cell with its column name for the cards
 *    layout, and puts each cell's content in one `.md-cell` element — all
 *    through DOM calls, never by editing the reply's HTML text, so nothing a
 *    model wrote can turn a label into markup;
 * 2. decides each box's layout from measured widths, and re-decides when a
 *    box actually changes width;
 * 3. keeps the scroll state's edge cues current and handles Copy table.
 *
 * Measuring forces a layout, so a table is measured only when its content
 * changed (during streaming, only the table being written). A resize only
 * re-reads box widths.
 */
export function useAdaptiveTables(root: RefObject<HTMLDivElement | null>, html: string): void {
  const memory = useRef(new Map<number, TableMemory>());

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const boxes = enhanceTables(el);
    if (boxes.length === 0) return;

    const seenWidths = new WeakMap<Element, number>();
    let seenFont = getComputedStyle(el).fontSize;

    const layoutAll = () => {
      layoutBoxes(boxes, memory.current);
      seenWidths.set(el, el.clientWidth);
      boxes.forEach((box) => seenWidths.set(box, box.clientWidth));
    };

    const remeasure = () => {
      // The reading text size changed or a web font arrived: same content,
      // different widths.
      memory.current.clear();
      layoutAll();
    };

    layoutAll();

    // `scroll` does not bubble; capture it from every table's scroll box.
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.classList.contains('md-table-scroll')) {
        const box = target.closest<HTMLElement>('.md-table');
        if (box && builtBoxes.has(box)) updateEdges(box);
      }
    };
    const onClick = (event: MouseEvent) => {
      const button = (event.target as Element | null)?.closest?.('button');
      // Only a button this hook built: a model can write the attribute, never our button.
      if (button && builtButtons.has(button) && el.contains(button)) void copyTable(el, button);
    };
    el.addEventListener('scroll', onScroll, true);
    el.addEventListener('click', onClick);
    document.fonts?.addEventListener?.('loadingdone', remeasure);

    let frame = 0;
    // Only a real width or text-size change re-decides: this drops the
    // observer's initial notification, height-only changes and the echo of
    // our own layout writes.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            const font = getComputedStyle(el).fontSize;
            const fontChanged = font !== seenFont;
            const widthChanged = entries.some(
              (entry) => seenWidths.get(entry.target) !== (entry.target as HTMLElement).clientWidth,
            );
            if (!fontChanged && !widthChanged) return;
            seenFont = font;
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(fontChanged ? remeasure : layoutAll);
          });
    observer?.observe(el);
    boxes.forEach((box) => observer?.observe(box));

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      el.removeEventListener('scroll', onScroll, true);
      el.removeEventListener('click', onClick);
      document.fonts?.removeEventListener?.('loadingdone', remeasure);
    };
  }, [root, html]);
}

function decideAll(plans: Plan[], widths: number[]): TableLayout[] {
  return plans.map((plan, i) =>
    decideTableLayout({
      needed: plan.needed,
      minimum: plan.minimum,
      box: widths[i],
      columns: plan.columns,
      current: plan.prior?.layout,
    }),
  );
}

function writeAll(plans: Plan[], layouts: TableLayout[]): void {
  plans.forEach(({ box }, i) => {
    if (box.dataset.layout !== layouts[i]) box.dataset.layout = layouts[i];
  });
}

/**
 * Measure, decide and write every box. Decide on the widths the boxes have
 * now, write, then read again: a shrink-to-fit host (a bubble sized by its
 * content) can change width with the layout it holds, so decide once more
 * on the settled width. Two passes at most, so it can never oscillate.
 */
function layoutBoxes(boxes: HTMLElement[], memory: Map<number, TableMemory>): void {
  const plans = measureAll(boxes, memory);
  let widths = plans.map(({ box }) => box.clientWidth);
  let layouts = decideAll(plans, widths);
  writeAll(plans, layouts);
  const settled = plans.map(({ box }) => box.clientWidth);
  if (settled.some((w, i) => w !== widths[i])) {
    widths = settled;
    layouts = decideAll(plans, widths);
    writeAll(plans, layouts);
  }
  plans.forEach((plan, i) => settlePlan(plan, layouts[i], widths[i], memory));
  boxes.forEach(updateEdges);
}

/** Per-table bookkeeping once its layout is written. */
function settlePlan(plan: Plan, layout: TableLayout, width: number, memory: Map<number, TableMemory>): void {
  const { box, index, signature, needed, minimum, prior } = plan;
  box.style.setProperty('--md-table-needed', `${needed}px`);
  // Measured live: at a 230px card a label column left values about 100px
  // wide and broke words mid-way; there the label goes on top.
  box.toggleAttribute('data-tight', width > 0 && width < CARD_LABEL_ABOVE_BELOW_PX);
  // A table that measured 0 was hidden (an ancestor with display:none):
  // remember nothing, so the pass that runs once it shows measures it for
  // real instead of trusting "needs 0px, always fits".
  if (needed <= 0 || width <= 0) {
    memory.delete(index);
    return;
  }
  // Fade the cards in only when a table SWITCHES to them, never on a first
  // render (a chat history full of tables shouldn't shimmer).
  if (layout === 'cards' && prior && prior.layout !== 'cards') {
    box.setAttribute('data-fresh', '');
    window.setTimeout(() => box.removeAttribute('data-fresh'), 400);
  }
  memory.set(index, { ...signature, needed, minimum, layout });
}

/** Above these, a renderer's tables are left as plain tables: adapting them would cost more than it gives. */
const MAX_TABLES = 50;
const MAX_CELLS = 20_000;

/** Boxes and buttons this hook built. Never trust a class or attribute a model can write. */
const builtBoxes = new WeakSet<HTMLElement>();
const builtButtons = new WeakSet<Element>();

interface Plan {
  box: HTMLElement;
  index: number;
  signature: TableSignature;
  columns: number;
  needed: number;
  minimum: number;
  prior: TableMemory | undefined;
}

/**
 * The widths each table is judged by. A table whose content is unchanged
 * reuses what it measured last time; the rest are measured in two batched
 * passes (every comfortable width, then every minimum), one forced layout
 * each however many tables changed. A table's natural width doesn't depend
 * on its siblings, so one pass for all of them is valid.
 */
function measureAll(boxes: HTMLElement[], memory: Map<number, TableMemory>): Plan[] {
  const plans = boxes.map((box, index): Plan & { table: HTMLTableElement; fresh: boolean } => {
    const table = box.querySelector('table') as HTMLTableElement;
    const signature = signatureOf(table);
    const prior = memory.get(index);
    const columns = headerRow(table)?.cells.length ?? table.rows[0]?.cells.length ?? 0;
    const fresh = !(prior && prior.needed > 0 && sameTableContent(prior, signature));
    return {
      box,
      table,
      index,
      signature,
      columns,
      prior,
      fresh,
      needed: prior?.needed ?? 0,
      minimum: prior?.minimum ?? 0,
    };
  });
  const toMeasure = plans.filter((p) => p.fresh);
  if (toMeasure.length > 0) {
    const comfortable = batchMeasure(toMeasure, 'comfortable');
    // Only a one- or two-column table is judged by its narrowest width.
    const narrow = toMeasure.filter((p) => p.columns <= 2);
    const minimum = batchMeasure(narrow, 'minimum');
    toMeasure.forEach((plan, i) => {
      const widths = rememberTableWidths(plan.prior, plan.signature, {
        needed: comfortable[i],
        minimum: minimum[narrow.indexOf(plan)] ?? 0,
      });
      plan.needed = widths.needed;
      plan.minimum = widths.minimum;
      plan.prior = widths.prior;
    });
  }
  return plans;
}

/**
 * `comfortable`: each table's natural width with long cells wrapped at the
 * readable measure the stylesheet sets for `[data-measure]`. `minimum`: its
 * width with every cell wrapped to its longest word. All writes, then all
 * reads, then the restore. `offsetWidth`, not a bounding rect, so a dialog
 * still scaling in doesn't shrink the reading. Runs inside a layout effect
 * or a frame, so the reader never sees the measuring state.
 */
function batchMeasure(
  plans: { box: HTMLElement; table: HTMLTableElement }[],
  kind: 'comfortable' | 'minimum',
): number[] {
  const before = plans.map(({ box }) => box.dataset.layout);
  plans.forEach(({ box }) => {
    box.dataset.layout = 'table';
    box.setAttribute('data-measure', kind);
  });
  const widths = plans.map(({ table }) => table.offsetWidth);
  plans.forEach(({ box }, i) => {
    box.removeAttribute('data-measure');
    const layout = before[i];
    if (layout) box.dataset.layout = layout;
  });
  return widths;
}

/** The header row: the thead's first row, or a first row made of `th` cells. */
function headerRow(table: HTMLTableElement): HTMLTableRowElement | undefined {
  const head = table.tHead?.rows[0];
  if (head) return head;
  const first = table.rows[0];
  return first && first.cells.length > 0 && Array.from(first.cells).every((c) => c.tagName === 'TH')
    ? first
    : undefined;
}

function signatureOf(table: HTMLTableElement): TableSignature {
  return {
    header: headerRow(table)?.textContent ?? '',
    rows: table.rows.length,
    textLength: table.textContent?.length ?? 0,
  };
}

/** The Copy table button: built from DOM nodes, both icons kept so they can cross-fade. */
function copyButton(): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'md-table-copy';
  const icons = document.createElement('span');
  icons.className = 'md-table-copy-icons';
  icons.setAttribute('aria-hidden', 'true');
  // Tabler "copy" and "check".
  icons.append(
    icon('md-table-copy-idle', [
      'M7 9.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z',
      'M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1',
    ]),
    icon('md-table-copy-done', ['M5 12l5 5l10 -10']),
  );
  const label = document.createElement('span');
  label.className = 'md-table-copy-label';
  label.textContent = 'Copy table';
  button.append(icons, label);
  return button;
}

function icon(className: string, paths: string[]): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  for (const d of paths) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}

/**
 * Wrap every top-level table (one not inside another table) in the
 * adaptive box. React replaces the root's content whenever the html
 * changes, so this normally sees fresh, unwrapped tables; a box it built
 * earlier (an effect re-run on the same html) is reused. A renderer with
 * more than `MAX_TABLES` tables or `MAX_CELLS` cells is left alone.
 * A table that fails to enhance stays a plain table instead of taking the
 * reply down with it.
 */
function enhanceTables(root: HTMLElement): HTMLElement[] {
  const tables = Array.from(root.querySelectorAll('table')).filter(
    (table) => !table.parentElement?.closest('table'),
  );
  if (tables.length > MAX_TABLES) return [];
  let cells = 0;
  for (const table of tables) {
    for (const row of Array.from(table.rows)) cells += row.cells.length;
    if (cells > MAX_CELLS) return [];
  }
  const boxes: HTMLElement[] = [];
  for (const table of tables) {
    const existing = table.parentElement?.closest<HTMLElement>('.md-table');
    if (existing && builtBoxes.has(existing) && root.contains(existing)) {
      boxes.push(existing);
      continue;
    }
    try {
      boxes.push(wrapTable(table));
    } catch {
      // Leave this one as the plain table it already is.
    }
  }
  return boxes;
}

function wrapTable(table: HTMLTableElement): HTMLElement {
  const box = document.createElement('div');
  box.className = 'md-table';
  // The scroll state until measured: if layout never runs, the reader
  // still never gets a page that scrolls sideways.
  box.dataset.layout = 'scroll';
  const bar = document.createElement('div');
  bar.className = 'md-table-bar';
  const button = copyButton();
  bar.append(button);
  const frame = document.createElement('div');
  frame.className = 'md-table-frame';
  const scroller = document.createElement('div');
  scroller.className = 'md-table-scroll';
  frame.append(scroller);
  box.append(bar, frame);
  labelCells(table);
  keepTableRoles(table);
  table.replaceWith(box);
  scroller.append(table);
  builtBoxes.add(box);
  builtButtons.add(button);
  return box;
}

/**
 * The cards layout sets `display: block` on the table's parts, and browsers
 * drop table semantics once the display type changes, so a screen reader
 * would hear a flat run of text on a phone. Explicit roles keep it a table
 * in every layout (FRONT-2585).
 */
function keepTableRoles(table: HTMLTableElement): void {
  table.setAttribute('role', 'table');
  for (const section of [table.tHead, ...Array.from(table.tBodies), table.tFoot]) {
    section?.setAttribute('role', 'rowgroup');
  }
  const head = headerRow(table);
  for (const row of Array.from(table.rows)) {
    row.setAttribute('role', 'row');
    const headerRole = row === head ? 'columnheader' : 'rowheader';
    for (const cell of Array.from(row.cells)) {
      cell.setAttribute('role', cell.tagName === 'TD' ? 'cell' : headerRole);
    }
  }
}

/**
 * Give each body cell its column name (shown in front of the value in the
 * cards layout) and wrap its content in one `.md-cell`, so a cell mixing
 * text with bold, code or links stays ONE value in the cards grid. The
 * label goes in through `dataset`, so it is only ever an attribute value.
 */
function labelCells(table: HTMLTableElement): void {
  const head = headerRow(table);
  const labels = head
    ? columnLabels(Array.from(head.cells, (cell) => ({ text: cell.textContent ?? '', span: cell.colSpan })))
    : [];
  for (const row of Array.from(table.rows)) {
    if (row === head) continue;
    let column = 0;
    for (const cell of Array.from(row.cells)) {
      const label = labels[column];
      if (label) cell.dataset.label = label;
      column += Math.max(1, cell.colSpan);
      const value = document.createElement('span');
      value.className = 'md-cell';
      // One node at a time: spreading a huge cell's children into one call overflows the stack.
      while (cell.firstChild) value.append(cell.firstChild);
      cell.append(value);
    }
  }
}

/**
 * Scroll state cues: a right-edge fade while more is hidden, a pinned-column
 * edge once scrolled. A box that really scrolls is also a named, focusable
 * region, so a keyboard user can reach its hidden columns with the arrow
 * keys (FRONT-2585); one that doesn't stays out of the tab order.
 */
function updateEdges(box: HTMLElement): void {
  const scroller = box.querySelector<HTMLElement>('.md-table-scroll');
  if (!scroller) return;
  const overflows = box.dataset.layout === 'scroll' && scroller.scrollWidth > scroller.clientWidth + 1;
  const left = scroller.scrollLeft;
  box.toggleAttribute('data-scrolled', overflows && left > 1);
  box.toggleAttribute('data-more', overflows && left + scroller.clientWidth < scroller.scrollWidth - 1);
  if (overflows) {
    scroller.tabIndex = 0;
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-label', 'Table, scrolls sideways');
  } else {
    scroller.removeAttribute('tabindex');
    scroller.removeAttribute('role');
    scroller.removeAttribute('aria-label');
  }
}

/**
 * A cell's text as the reader sees it: `innerText` keeps a line break, a
 * list item or a paragraph inside a cell as a break (flattened to a space
 * later) instead of gluing the words together, and leaves out text that
 * isn't shown.
 */
function cellText(cell: HTMLTableCellElement): string {
  return cell.innerText ?? '';
}

function tableRows(table: HTMLTableElement): string[][] {
  return Array.from(table.rows, (row) => Array.from(row.cells, cellText));
}

/**
 * Put the table on the clipboard twice over: tab-separated text (Excel and
 * Sheets split it into columns) and a plain HTML table (Word, Outlook and
 * Notion paste a real table). Falls back to the text alone where the browser
 * can't write rich clipboard items.
 */
async function writeTable(rows: string[][]): Promise<boolean> {
  // Guard on presence, not `?.`: an insecure context has no clipboard and an
  // optional call would report success with nothing copied.
  if (!navigator.clipboard) return false;
  const tsv = tableToTsv(rows);
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([tsv], { type: 'text/plain' }),
          'text/html': new Blob([tableToHtml(rows)], { type: 'text/html' }),
        }),
      ]);
      return true;
    }
  } catch {
    // Some browsers refuse rich items but allow plain text; try that below.
  }
  try {
    await navigator.clipboard.writeText(tsv);
    return true;
  } catch {
    return false;
  }
}

const resetTimers = new WeakMap<HTMLButtonElement, number>();

async function copyTable(root: HTMLElement, clicked: HTMLButtonElement): Promise<void> {
  const box = clicked.closest('.md-table');
  const table = box?.querySelector('table');
  if (!box || !table) return;
  // A streaming answer replaces the rendered html while the clipboard write
  // is in flight, detaching the button that was clicked; confirm on the
  // button now at the same place instead.
  const index = Array.from(root.querySelectorAll('.md-table')).indexOf(box);
  if (!(await writeTable(tableRows(table)))) {
    toast.error('Could not copy the table');
    return;
  }
  const button = clicked.isConnected
    ? clicked
    : root.querySelectorAll('.md-table')[index]?.querySelector<HTMLButtonElement>('.md-table-copy');
  if (!button) return;
  const label = button.querySelector('.md-table-copy-label');
  button.dataset.state = 'done';
  if (label) label.textContent = 'Copied';
  // A second click restarts the confirmation instead of being cut short by the first click's timer.
  window.clearTimeout(resetTimers.get(button));
  resetTimers.set(
    button,
    window.setTimeout(() => {
      delete button.dataset.state;
      if (label) label.textContent = 'Copy table';
    }, 1600),
  );
}
